/**
 * Session Manager Service
 *
 * Centralized handling for session timeout and token refresh:
 * - Detects 401 unauthorized responses and triggers logout
 * - Implements automatic token refresh before expiry
 * - Provides periodic auth status checks
 * - Shows toast notifications for session events
 */

import { logout as logoutStore, getAuthState, selectIsTokenExpired, updateAccessToken } from './auth-store';
import { logout as authServiceLogout, checkAuthStatus } from './auth-service';
import { toastWarning } from '../components/toast';
import { apiClient } from './api-client';

/**
 * Session manager configuration
 */
interface SessionManagerConfig {
  /** Interval in ms for periodic auth checks (default: 5 minutes) */
  authCheckInterval: number;
  /** Time in ms before token expiry to trigger refresh (default: 2 minutes) */
  refreshBeforeExpiry: number;
  /** Whether to show toast notifications (default: true) */
  showNotifications: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: SessionManagerConfig = {
  authCheckInterval: 5 * 60 * 1000, // 5 minutes
  refreshBeforeExpiry: 2 * 60 * 1000, // 2 minutes
  showNotifications: true,
};

/**
 * Session manager state
 */
let config: SessionManagerConfig = { ...DEFAULT_CONFIG };
let authCheckIntervalId: number | null = null;
let refreshTimeoutId: number | null = null;
let isInitialized = false;
let isLoggingOut = false;

/**
 * Handle unauthorized response (401)
 * Clears local auth state and redirects to login
 */
export async function handleUnauthorized(): Promise<void> {
  // Prevent multiple logout calls
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  try {
    // Clear intervals/timeouts
    stopSessionManager();

    // Show notification
    if (config.showNotifications) {
      toastWarning('Your session has expired. Please log in again.', {
        title: 'Session Expired',
        duration: 5000,
      });
    }

    // Clear local auth state
    logoutStore();

    // Small delay to let toast show
    await new Promise(resolve => setTimeout(resolve, 100));

    // Redirect to login page with session_expired flag to prevent redirect loops
    // The flag ensures the login page clears any stale auth state from session storage
    window.location.href = '/login?session_expired=1';
  } finally {
    isLoggingOut = false;
  }
}

/**
 * Handle logout with redirect to Azure AD
 */
export async function handleLogout(): Promise<void> {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  try {
    // Clear intervals/timeouts
    stopSessionManager();

    // Call auth service logout (handles Azure AD redirect)
    await authServiceLogout(true);
  } catch (error) {
    console.error('Error during logout:', error);
    // Fallback: clear local state and redirect to login
    logoutStore();
    window.location.href = '/login';
  } finally {
    isLoggingOut = false;
  }
}

/**
 * Attempt to refresh the session token
 */
async function refreshSession(): Promise<boolean> {
  try {
    const response = await apiClient.post<{ success: boolean; expiresAt: number }>('/auth/refresh', undefined, {
      credentials: 'include',
    });

    if (response.data.success && response.data.expiresAt) {
      // Calculate expiresIn from the absolute expiresAt timestamp
      const expiresIn = Math.max(0, Math.floor((response.data.expiresAt - Date.now()) / 1000));
      // Update the token expiry in the auth store so scheduleTokenRefresh uses the correct time
      updateAccessToken('', expiresIn);
      console.log(`Session refreshed successfully, expires in ${expiresIn}s`);
      scheduleTokenRefresh();
      return true;
    }
  } catch (error) {
    console.warn('Failed to refresh session:', error);
  }

  return false;
}

/**
 * Schedule token refresh before expiry
 */
function scheduleTokenRefresh(): void {
  // Clear existing timeout
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  const authState = getAuthState();

  if (!authState.isAuthenticated || !authState.tokenExpiresAt) {
    return;
  }

  const now = Date.now();
  const expiresAt = authState.tokenExpiresAt;
  const refreshAt = expiresAt - config.refreshBeforeExpiry;

  if (refreshAt > now) {
    const delay = refreshAt - now;
    console.log(`Scheduling token refresh in ${Math.round(delay / 1000)}s`);

    refreshTimeoutId = window.setTimeout(async () => {
      const success = await refreshSession();
      if (!success) {
        // Token refresh failed, check if user is still authenticated
        const isAuth = await checkAuthStatus();
        if (!isAuth) {
          await handleUnauthorized();
        }
      }
    }, delay);
  } else if (expiresAt > now) {
    // Token is about to expire, refresh immediately
    refreshSession().catch(console.error);
  }
}

/**
 * Periodic auth status check
 * Verifies the session is still valid with the backend
 */
async function performAuthCheck(): Promise<void> {
  const authState = getAuthState();

  // Skip if not authenticated
  if (!authState.isAuthenticated) {
    return;
  }

  // Check if token appears expired locally
  if (selectIsTokenExpired(authState)) {
    // Try to refresh first
    const refreshed = await refreshSession();
    if (!refreshed) {
      // Check with backend
      const isAuth = await checkAuthStatus();
      if (!isAuth) {
        await handleUnauthorized();
      }
    }
    return;
  }

  // Verify with backend periodically
  try {
    const isAuth = await checkAuthStatus();
    if (!isAuth) {
      await handleUnauthorized();
    }
  } catch (error) {
    console.warn('Auth check failed:', error);
    // Don't immediately log out on network errors
    // The 401 handler will catch actual session expiry
  }
}

/**
 * Start periodic auth checks
 */
function startPeriodicAuthCheck(): void {
  if (authCheckIntervalId) {
    return;
  }

  authCheckIntervalId = window.setInterval(performAuthCheck, config.authCheckInterval);
}

/**
 * Stop periodic auth checks
 */
function stopPeriodicAuthCheck(): void {
  if (authCheckIntervalId) {
    clearInterval(authCheckIntervalId);
    authCheckIntervalId = null;
  }
}

/**
 * Initialize the session manager
 * Call this during application startup after auth is checked
 */
export function initSessionManager(options?: Partial<SessionManagerConfig>): void {
  if (isInitialized) {
    return;
  }

  // Merge config
  config = { ...DEFAULT_CONFIG, ...options };

  // Configure the API client with unauthorized handler
  configureApiClient();

  // Start periodic auth checks
  startPeriodicAuthCheck();

  // Schedule token refresh if authenticated
  const authState = getAuthState();
  if (authState.isAuthenticated) {
    scheduleTokenRefresh();
  }

  isInitialized = true;
  console.log('Session manager initialized');
}

/**
 * Configure the API client with session handling
 */
function configureApiClient(): void {
  // Add response interceptor to catch 401 errors consistently
  apiClient.addErrorInterceptor(async (error, request) => {
    // Handle 401 unauthorized
    if (error.status === 401) {
      // Don't trigger logout for auth endpoints (they handle their own errors)
      // Check the request URL, not the error message
      const isAuthEndpoint = request.url.includes('/auth/');
      if (!isAuthEndpoint) {
        await handleUnauthorized();
      }
    }
    return error;
  });
}

/**
 * Stop the session manager
 * Call this during logout or app cleanup
 */
export function stopSessionManager(): void {
  stopPeriodicAuthCheck();

  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  isInitialized = false;
}

/**
 * Restart session monitoring after login
 */
export function restartSessionMonitoring(): void {
  stopSessionManager();
  isInitialized = false;
  initSessionManager(config);
}

/**
 * Get current session manager config
 */
export function getSessionManagerConfig(): Readonly<SessionManagerConfig> {
  return { ...config };
}

/**
 * Check if session manager is initialized
 */
export function isSessionManagerInitialized(): boolean {
  return isInitialized;
}
